import { useEffect, useState } from 'react'
import { Form, Card, Input, ExpandableSection, Grid } from '@enterprise-ui/canvas-ui-react'
import { useEnv } from '@praxis/component-runtime-env'
import { MODAL_TYPES } from 'config/constants'
import { IDeleteModalData } from 'components/modals/DeleteModal/DeleteModal'
import './FileDrawer.scss'

import { FileViewUI, IIndeterminateStatus, setModalData, setModalType, useAsset, useFiles } from 'store'
import { useDispatch } from 'react-redux'
import { FileView } from '@galleryjs/api-digital-assets'
import { globalDrawerState } from './FileTable'
import EnterpriseIcon, { ErrorFilledIcon } from '@enterprise-ui/icons'
import DraggableFile from './DraggableFile'
import { some } from 'lodash'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

type IFileDrawerProps = {
  files: FileViewUI[]
  title: string
  thumbnailFile: any
  allDrawersState: globalDrawerState
  setAllDrawersState: React.Dispatch<React.SetStateAction<globalDrawerState>>
}

const FileDrawer = (props: IFileDrawerProps) => {
  const { allDrawersState, setAllDrawersState, title, files, thumbnailFile } = props
  const { LODProfileTypeDescriptions, bucketType } = useEnv()
  const { deleteFile, bulkSelectFiles } = useFiles()
  const { assetDetailPageAsset: asset } = useAsset()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [hasInvalidFiles, setHasInvalidFiles] = useState<boolean>(false)
  const [drawerStatus, setDrawerStatus] = useState<IIndeterminateStatus>({
    status: 0,
    indeterminate: false,
    filesSelected: 0,
  })

  const dispatch = useDispatch()

  const paddingStyle = {
    padding: '6px',
  }

  //Checks for Invalid files
  const checkForInvalidFiles = (files: FileView[]) => {
    const invalidFiles = files.filter(file => file.validationResult !== undefined && file.validationResult.length > 0)
    if (invalidFiles.length > 0) {
      setHasInvalidFiles(true)
    }
  }

  const deleteAlertModal = (file: FileView) => {
    //// TODO: MAKE SURE THAT ASSET IS UPDATED AFTER FILE DELETE
    const triggerDelete = () => {
      deleteFile(asset!!.id, [file.id])
    }
    const modalData: IDeleteModalData = {
      data: file,
      triggerDelete,
      bodyText: `"${file.name}" will be completely removed from Gallery.`,
      onRefuseText: 'Cancel',
      onApproveText: 'Delete File',
      headingText: 'Delete this file?',
    }
    dispatch(setModalType(MODAL_TYPES.DELETE_FILE))
    dispatch(setModalData(modalData))
  }

  useEffect(() => {
    switch (allDrawersState) {
      case globalDrawerState.ALL_CLOSED:
        setDrawerOpen(false)
        break
      case globalDrawerState.ALL_OPEN:
        setDrawerOpen(true)
        break
    }
  }, [allDrawersState])

  useEffect(() => {
    if (
      (drawerOpen && allDrawersState === globalDrawerState.ALL_CLOSED) ||
      (!drawerOpen && allDrawersState === globalDrawerState.ALL_OPEN)
    ) {
      setAllDrawersState(globalDrawerState.INTERMEDIATE)
    }
  }, [drawerOpen])

  useEffect(() => {
    const filesSelected = files.filter(file => file.selected)
    const percentSelected = filesSelected.length / files.length
    const status = {
      status: percentSelected,
      indeterminate: percentSelected < 1 && percentSelected > 0,
      filesSelected: filesSelected.length,
    }
    setDrawerStatus(status)
    checkForInvalidFiles(files)
  }, [files])

  const extractFileNameData = (fileName: string): string => {
    const nameWithoutExt = fileName.split('.')[0]
    const parts = nameWithoutExt.split('_')
    // Create a lookup map with lowercase keys for case-insensitive matching
    const lowerCaseLODKeys = Object.keys(LODProfileTypeDescriptions).reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = key // Map lowercase keys to original keys
        return acc
      },
      {} as Record<string, string>
    )
    // Iterate from the back and find the first matching LOD key
    for (let i = parts.length - 1; i >= 0; i--) {
      const potentialKey = parts.slice(i).join('_')
      const lowerCaseKey = potentialKey.toLowerCase()
      if (lowerCaseKey in lowerCaseLODKeys) {
        return lowerCaseLODKeys[lowerCaseKey] // Return the original key from the dictionary
      }
    }
    return ''
  }

  const getTooltip = (fileName: string, fileExtension?: string) => {
    const profileType = extractFileNameData(fileName)
    return profileType in LODProfileTypeDescriptions
      ? LODProfileTypeDescriptions[profileType]
      : `This file is the ${fileExtension?.toUpperCase()} source file for this asset.`
  }

  return (
    <Card elevation={0} className="drawer">
      <div style={paddingStyle} className="hc-pa-normal filedrawer-expandable-container">
        <Grid.Container direction="column" spacing="dense">
          <ExpandableSection
            padding="dense"
            className="file-drawer"
            expanded={drawerOpen}
            onExpand={() => {
              setDrawerOpen(!drawerOpen)
            }}
          >
            <Grid.Item xs={12}>
              <Grid.Container spacing="dense" align="center">
                <Grid.Item>
                  <Grid.Container spacing="dense">
                    <Grid.Item>
                      <Input.Checkbox
                        key={`drawer-${title}`}
                        id={`drawer-${title}`}
                        data-testid={`drawer_checkbox_${title}`}
                        label={title}
                        className="drawer-checkbox"
                        checked={drawerStatus.status > 0}
                        indeterminate={drawerStatus.indeterminate}
                        onChange={() =>
                          bulkSelectFiles(
                            files.map((file: FileViewUI) => file.id),
                            drawerStatus.status === 1 ? false : true
                          )
                        }
                      />
                    </Grid.Item>
                    <Grid.Item className="drawer-avatar">
                      <span data-testid={`drawer_filecount_${title}`}>{files.length} files</span>
                    </Grid.Item>
                  </Grid.Container>
                </Grid.Item>
                {hasInvalidFiles && title === bucketType.REAL_TIME_ASSET && (
                  <Grid.Item className="hc-mr-xl hc-pr-min hc-mb-min">
                    <EnterpriseIcon size="sm" icon={ErrorFilledIcon} className="hc-clr-error" />
                  </Grid.Item>
                )}
              </Grid.Container>
            </Grid.Item>
            <ExpandableSection.Content>
              <Form>
                <Grid.Item xs={12}>
                  {some(files) && (
                    <Grid.Item xs={12}>
                      {files!!.map((file: any) => {
                        return (
                          <DndProvider backend={HTML5Backend}>
                            <DraggableFile
                              key={file.id}
                              file={file}
                              title={title}
                              deleteAlertModal={deleteAlertModal}
                              getTooltip={getTooltip}
                              thumbnailFile={thumbnailFile}
                            />
                          </DndProvider>
                        )
                      })}
                    </Grid.Item>
                  )}
                </Grid.Item>
              </Form>
            </ExpandableSection.Content>
          </ExpandableSection>
        </Grid.Container>
      </div>
    </Card>
  )
}

export default FileDrawer
